import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["description", "panel", "tab", "title"]

  connect() {
    this.transitionTimers = []
    const activeTab = this.tabTargets.find((tab) => tab.classList.contains("active")) || this.tabTargets[0]
    if (activeTab) {
      this.showProduct(activeTab.dataset.creditPricingProductParam)
    }
  }

  disconnect() {
    this.clearTransitionTimers()
  }

  switch(event) {
    event.preventDefault()
    this.activate(event.params.product, { animate: true })
  }

  navigateTabs(event) {
    const currentIndex = this.tabTargets.indexOf(event.currentTarget)
    if (currentIndex === -1) return

    const nextIndex = this.nextTabIndex(event.key, currentIndex)
    if (nextIndex === null) return

    event.preventDefault()

    const nextTab = this.tabTargets[nextIndex]
    nextTab.focus()
    this.activate(nextTab.dataset.creditPricingProductParam, { animate: true })
  }

  activate(product, { animate = false } = {}) {
    const selectedTab = this.tabTargets.find((tab) => tab.dataset.creditPricingProductParam === product)
    if (!selectedTab) return

    this.clearTransitionTimers()
    this.updateTabs(product)

    if (!animate || this.prefersReducedMotion()) {
      this.updateCopy(selectedTab)
      this.showProduct(product)
      return
    }

    const incomingPanel = this.panelFor(product)
    const outgoingPanel = this.panelTargets.find((panel) => !panel.hidden)
    if (!incomingPanel || incomingPanel === outgoingPanel) {
      this.updateCopy(selectedTab)
      this.showProduct(product)
      return
    }

    const container = outgoingPanel.parentElement
    container.style.minHeight = `${outgoingPanel.offsetHeight}px`
    this.element.classList.add("is-switching")
    outgoingPanel.classList.remove("is-active")

    this.schedule(() => {
      outgoingPanel.hidden = true
      this.updateCopy(selectedTab)
      incomingPanel.hidden = false
      incomingPanel.classList.remove("is-active")

      window.requestAnimationFrame(() => {
        incomingPanel.classList.add("is-active")
        this.schedule(() => {
          container.style.minHeight = ""
          this.element.classList.remove("is-switching")
        }, 180)
      })
    }, 120)
  }

  showProduct(product) {
    const selectedTab = this.tabTargets.find((tab) => tab.dataset.creditPricingProductParam === product)
    if (selectedTab) this.updateCopy(selectedTab)

    this.updateTabs(product)
    this.panelTargets.forEach((panel) => {
      const active = panel.dataset.product === product
      panel.hidden = !active
      panel.classList.toggle("is-active", active)
    })
  }

  updateTabs(product) {
    this.tabTargets.forEach((tab) => {
      const active = tab.dataset.creditPricingProductParam === product
      tab.classList.toggle("active", active)
      tab.setAttribute("aria-selected", active.toString())
      tab.tabIndex = active ? 0 : -1
    })
  }

  updateCopy(tab) {
    this.titleTarget.textContent = tab.dataset.creditPricingTitleParam
    this.descriptionTarget.textContent = tab.dataset.creditPricingDescriptionParam
  }

  panelFor(product) {
    return this.panelTargets.find((panel) => panel.dataset.product === product)
  }

  nextTabIndex(key, currentIndex) {
    const tabCount = this.tabTargets.length
    if (tabCount === 0) return null

    switch (key) {
      case "ArrowRight":
      case "ArrowDown":
        return (currentIndex + 1) % tabCount
      case "ArrowLeft":
      case "ArrowUp":
        return (currentIndex - 1 + tabCount) % tabCount
      case "Home":
        return 0
      case "End":
        return tabCount - 1
      default:
        return null
    }
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches
  }

  schedule(callback, delay) {
    const timer = window.setTimeout(callback, delay)
    this.transitionTimers.push(timer)
  }

  clearTransitionTimers() {
    this.transitionTimers?.forEach((timer) => window.clearTimeout(timer))
    this.transitionTimers = []
    this.element.classList.remove("is-switching")
    this.panelTargets[0]?.parentElement.style.removeProperty("min-height")
  }
}
